/**
 * "Use for the weekly report cover" on a project photo in the Aircraft category (MB's decisions 665 and 712;
 * docs/specs/cloud/project-photos.md §13), for tenant admins, managers and office staff: the weekly report's cover
 * shows this photo every week, whichever aircraft flew, before the project's own cover image and the aircraft's
 * register photo. One photo per project: choosing another moves the cover to it. Laptops get it with their next sync.
 * The database decides (`project_photo_weekly_cover`).
 */
import { useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Button } from '../components/Button.tsx';
import { setWeeklyCover } from './photoApi.ts';

export interface UseForWeeklyCoverProps {
  readonly sb: SupabaseClient;
  readonly photoId: string;
  /** The photo's title (caption or file name), to name it. */
  readonly title: string;
  /** Whether this photo is the project's weekly report cover now. */
  readonly current: boolean;
  /** May the viewer change it (tenant admins, managers, office staff; not on an archived project). */
  readonly mayChange: boolean;
  readonly onChanged: (photoId: string, cover: boolean) => void;
}

const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function UseForWeeklyCover({
  sb,
  photoId,
  title,
  current,
  mayChange,
  onChanged,
}: UseForWeeklyCoverProps): ReactElement | null {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function change(cover: boolean): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      await setWeeklyCover(sb, photoId, cover);
      onChanged(photoId, cover);
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setBusy(false);
    }
  }

  if (!current && !mayChange) return null;
  return (
    <div className="stack">
      <div className="row row--wrap">
        {current ? (
          <>
            <span className="chip">Weekly report cover</span>
            {mayChange && (
              <Button
                variant="quiet"
                busy={busy}
                aria-label={`Stop using ${title} for the weekly report cover`}
                onClick={() => void change(false)}
              >
                Stop using for the cover
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="quiet"
            busy={busy}
            aria-label={`Use ${title} for the weekly report cover`}
            onClick={() => void change(true)}
          >
            Use for the weekly report cover
          </Button>
        )}
      </div>
      {problem && (
        <p className="notice notice--fail" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
